/**
 * URL Builder Utilities
 * Functions for generating locale-aware URLs for content
 */

import { defaultLocale, locales, type Locale } from "@/i18n/config";
import { type DocumentType } from "./sanity-routes.config";

/**
 * Localized slug object structure from Sanity
 * Dynamically typed based on configured locales
 */
export type LocalizedSlug = {
  [K in Locale]?: { current?: string };
};

/**
 * Build a content URL with optional locale prefix
 *
 * For the default locale (en), no prefix is added.
 * For other locales (af), the locale is prefixed to the path.
 *
 * @example
 * buildContentUrl('/about', 'en') // '/about'
 * buildContentUrl('/about', 'af') // '/af/about'
 * buildContentUrl('/oor-ons', 'af') // '/af/oor-ons'
 */
export function buildContentUrl(slug: string, locale: Locale): string {
  // Ensure slug starts with /
  const normalizedSlug = slug.startsWith("/") ? slug : `/${slug}`;

  // Default locale doesn't need a prefix
  if (locale === defaultLocale) {
    return normalizedSlug;
  }

  // Other locales get prefixed
  return `/${locale}${normalizedSlug}`;
}

/**
 * Get the localized path from a content URL
 * Strips the locale prefix if present
 *
 * @example
 * getLocalizedPath('/af/oor-ons') // { path: '/oor-ons', locale: 'af' }
 * getLocalizedPath('/about') // { path: '/about', locale: 'en' }
 */
export function getLocalizedPath(url: string): {
  path: string;
  locale: Locale;
} {
  const match = url.match(/^\/([a-z]{2})(\/.*)?$/);

  if (match) {
    const potentialLocale = match[1] as Locale;
    // Check if this is a valid non-default locale
    if (
      locales.includes(potentialLocale) &&
      potentialLocale !== defaultLocale
    ) {
      return {
        path: match[2] || "/",
        locale: potentialLocale,
      };
    }
  }

  return {
    path: url,
    locale: defaultLocale,
  };
}

/**
 * Get the localized slug from a localizedSlug object
 *
 * Falls back to default locale if the requested locale's slug is not defined.
 *
 * @example
 * getLocalizedSlug({ en: { current: '/about' }, af: { current: '/oor-ons' } }, 'af')
 * // '/oor-ons'
 *
 * getLocalizedSlug({ en: { current: '/about' } }, 'af')
 * // '/about' (fallback to en)
 */
export function getLocalizedSlug(
  slug: LocalizedSlug | null | undefined,
  locale: Locale,
): string | null {
  if (!slug) return null;

  // Try requested locale first
  const localizedSlug = slug[locale]?.current;
  if (localizedSlug) return localizedSlug;

  // Fallback to default locale
  const defaultSlug = slug[defaultLocale]?.current;
  if (defaultSlug) return defaultSlug;

  return null;
}

/**
 * Check if a slug has a translation for a given locale
 */
export function hasLocaleSlug(
  slug: LocalizedSlug | null | undefined,
  locale: Locale,
): boolean {
  return Boolean(slug?.[locale]?.current);
}

/**
 * Build a full URL for a document with a given type and slug
 */
export function buildDocumentUrl(
  docType: DocumentType,
  slug: string,
  locale: Locale,
): string {
  if (docType === "home") {
    return buildContentUrl("/", locale);
  }

  // For nested routes, the slug already includes the full path
  return buildContentUrl(slug, locale);
}

/**
 * Get alternate locale URLs for a document
 * Useful for generating hreflang links
 */
export function getAlternateUrls(
  slug: LocalizedSlug | null | undefined,
): { locale: Locale; url: string }[] {
  if (!slug) return [];

  const alternates: { locale: Locale; url: string }[] = [];

  for (const locale of locales) {
    const localeSlug = slug[locale]?.current;
    if (localeSlug) {
      alternates.push({
        locale,
        url: buildContentUrl(localeSlug, locale),
      });
    }
  }

  return alternates;
}
